import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link2, Phone, Mail, Loader2, Eye, Pencil, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { getFamilyForPatient, clearViewedFamily } from '../../redux/familySlice';
import { getFileURL } from '../../utils/fileURL';
import ModalShell from '../common/ModalShell';

// Doctor / admin view: marks a patient who is linked to somebody, and opens the
// detail on click.
//
// TWO COLUMNS, BECAUSE SHARING HAS A DIRECTION.
//
// This panel used to ask one question — "what can this patient open?" — which is
// EMPTY for a secondary, since a secondary holds no shares as grantee. A child
// linked to their parent therefore opened a panel containing only the child, and
// the parent, the one thing the clinic actually needs, was missing entirely.
//
//   Managed by      who can open THEM     <- for a secondary, the parent
//   They can open   what THEY can open    <- their secondaries, plus anyone sharing
//
// Those cannot be merged into one list without implying access is mutual, which
// is the exact thing the sharing model exists to stop being true.
//
// WHY THE CONTACT PERSON MATTERS. A secondary has no email or phone of their
// own — they are reached through whoever manages them. Without that, a clinic
// ringing about a child's appointment sees a blank number and is stuck.
export default function LinkedAccountChip({ patient }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { viewed, viewedLoading } = useSelector((s) => s.family || {});
    const [open, setOpen] = useState(false);

    // ONLY WHEN A LINK EXISTS TODAY.
    //
    // This used to read `primaryPatientId`, a column left over from the group
    // model. Revoking a share never cleared it, so patients who had been
    // separated still showed "Linked account" here. `isLinked` is computed from
    // live shares by the list endpoint, and the old column is NOT used as a
    // fallback — falling back would restore exactly the wrong answer.
    if (!patient?.isLinked) return null;

    const openPanel = () => {
        setOpen(true);
        dispatch(getFamilyForPatient(patient.patientId));
    };

    const close = () => {
        setOpen(false);
        dispatch(clearViewedFamily());
    };

    const managedBy = viewed?.managedBy || [];
    const theyCanOpen = viewed?.theyCanOpen || [];
    // Whoever manages them is who to ring. A secondary has no number of their
    // own, so the first manager with contact details is the useful one.
    const contact = managedBy.find((m) => m.phone || m.email) || null;

    const permChip = (perm) => (
        perm === 'readwrite' ? (
            <span className="inline-flex items-center gap-0.5 rounded bg-[#4ABDBD]/15 px-1 py-px text-[9px] font-semibold text-[#3AA8A8]">
                <Pencil className="h-2.5 w-2.5" />{t('family.read_write', 'Read & write')}
            </span>
        ) : (
            <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1 py-px text-[9px] font-semibold text-gray-500">
                <Eye className="h-2.5 w-2.5" />{t('family.read_only', 'Read only')}
            </span>
        )
    );

    const personRow = (p) => (
        <div key={p.patientId} className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5">
            {p.image ? (
                <img src={getFileURL(p.image)} alt="" className="h-7 w-7 flex-shrink-0 rounded-full object-cover" />
            ) : (
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15 text-[10px] font-bold text-[#3AA8A8]">
                    {(p.name || '?').charAt(0).toUpperCase()}
                </span>
            )}
            <span className="min-w-0 flex-1">
                <span className="block truncate text-[12px] font-semibold text-gray-800">{p.name}</span>
                <span className="flex flex-wrap items-center gap-1 text-[10px] text-gray-400">
                    <span>
                        {p.accountType === 'secondary'
                            ? t('family.secondary', 'Secondary')
                            : t('family.primary', 'Primary')}
                    </span>
                    {permChip(p.permission)}
                </span>
            </span>
        </div>
    );

    const column = (icon, title, note, rows, emptyText) => (
        <div className="flex min-w-0 flex-col gap-1.5">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                {icon}{title}
            </p>
            <p className="text-[10px] leading-relaxed text-gray-400">{note}</p>
            {rows.length === 0
                ? <p className="rounded-lg bg-gray-50 px-2 py-3 text-center text-[10px] text-gray-400">{emptyText}</p>
                : rows.map(personRow)}
        </div>
    );

    return (
        <>
            <button
                type="button"
                data-testid={`linked-account-chip-${patient.patientId}`}
                onClick={openPanel}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-[#4ABDBD]/10 px-2 py-0.5 text-[11px] font-semibold text-[#2D8A8A] transition hover:bg-[#4ABDBD]/20"
                title={t('family.linked_tooltip', 'This patient is linked to another account')}
            >
                <Link2 className="h-3 w-3" />
                {t('family.linked_account', 'Linked account')}
            </button>

            {/* Portalled for the same reason as the other family modals: a
                position:fixed overlay is contained by any ancestor with a CSS
                transform, which silently squeezes it into that element's box. */}
            {open && createPortal(
                <ModalShell
                    title={(
                        <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate">
                                {viewed?.patient?.name || t('family.group_title', 'Linked accounts')}
                            </span>
                            {viewed?.patient && (
                                <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                                    {viewed.patient.accountType === 'secondary'
                                        ? t('family.secondary', 'Secondary')
                                        : t('family.primary', 'Primary')}
                                </span>
                            )}
                        </span>
                    )}
                    onClose={close}
                    size="2xl"
                    zClass="z-[9999]"
                    contentTestId="family-panel"
                >
                        {viewedLoading ? (
                            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {t('family.loading', 'Loading...')}
                            </div>
                        ) : !viewed ? (
                            // An empty panel reads as a broken feature. Say what
                            // happened instead.
                            <div className="py-6 text-center">
                                <p className="text-sm font-semibold text-[#1a365d]">
                                    {t('family.group_unavailable', 'Details unavailable')}
                                </p>
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('family.group_unavailable_hint', 'This could not be loaded. The other person may not be a patient at this hospital.')}
                                </p>
                            </div>
                        ) : (
                            <>
                                {contact && (
                                    <div className="mb-4 rounded-xl border border-[#4ABDBD]/25 bg-[#4ABDBD]/8 px-3 py-2.5">
                                        <p className="mb-1 text-[11px] font-bold text-[#2D8A8A]">
                                            {t('family.contact_person', 'Contact person')}
                                        </p>
                                        <p className="text-xs font-semibold text-gray-800">{contact.name}</p>
                                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-gray-600">
                                            {contact.phone && (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Phone className="h-3 w-3" /> {contact.phone}
                                                </span>
                                            )}
                                            {contact.email && (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Mail className="h-3 w-3" /> {contact.email}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Two columns on a wide screen, stacked on a
                                    narrow one — this opens on tablets at
                                    reception as often as on a desktop. */}
                                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    {column(
                                        <ArrowUpRight className="h-3 w-3" />,
                                        t('family.managed_by', 'Managed by'),
                                        t('family.managed_by_note', 'People who can open this patient’s account.'),
                                        managedBy,
                                        t('family.managed_by_none', 'Nobody can open this account.')
                                    )}
                                    {column(
                                        <ArrowDownLeft className="h-3 w-3" />,
                                        t('family.they_can_open', 'This patient can open'),
                                        t('family.they_can_open_note', 'Their own secondary accounts, and anyone sharing with them.'),
                                        theyCanOpen,
                                        t('family.they_can_open_none', 'This patient cannot open anybody else.')
                                    )}
                                </div>
                            </>
                        )}
                </ModalShell>,
                document.body
            )}
        </>
    );
}
